import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

interface CreditWallet {
  balance: number;
  company_id: string;
}

type CompanyRole = string | null;

export function useCompanyCredits() {
  const { user } = useAuth();
  const [wallet, setWallet] = useState<CreditWallet | null>(null);
  const [loading, setLoading] = useState(true);
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [companyRole, setCompanyRole] = useState<CompanyRole>(null);

  const userId = user?.id;

  // Resolve user's company
  useEffect(() => {
    if (!userId) { setCompanyId(null); setLoading(false); return; }

    (async () => {
      const { data } = await supabase
        .from('company_users')
        .select('company_id, role')
        .eq('user_id', userId)
        .eq('is_active', true)
        .limit(1)
        .maybeSingle();

      setCompanyId(data?.company_id ?? null);
      setCompanyRole(data?.role ?? null);
    })();
  }, [userId]);

  // Fetch wallet once we have company
  useEffect(() => {
    if (!companyId) { setWallet(null); setLoading(false); return; }

    let cancelled = false;

    const fetchWallet = async () => {
      const { data } = await supabase
        .from('company_credit_wallets')
        .select('balance, company_id')
        .eq('company_id', companyId)
        .maybeSingle();

      if (!cancelled) {
        setWallet(data ? { balance: data.balance, company_id: data.company_id } : null);
        setLoading(false);
      }
    };

    setLoading(true);
    fetchWallet();

    // Realtime subscription for balance changes
    const channel = supabase
      .channel(`credits-${companyId.slice(-8)}`)
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'company_credit_wallets', filter: `company_id=eq.${companyId}` },
        (payload) => {
          const row = payload.new as any;
          if (row?.balance !== undefined) {
            setWallet({ balance: row.balance, company_id: companyId });
          }
        }
      )
      .subscribe((status) => { if (status === 'SUBSCRIBED') fetchWallet(); });

    // Focus / visibility self-heal.
    let focusTimer: ReturnType<typeof setTimeout> | null = null;
    const runDebounced = () => {
      if (focusTimer) return;
      focusTimer = setTimeout(() => { focusTimer = null; fetchWallet(); }, 500);
    };
    const onFocus = () => runDebounced();
    const onVis = () => { if (document.visibilityState === 'visible') runDebounced(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVis);

    return () => {
      cancelled = true;
      supabase.removeChannel(channel);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVis);
      if (focusTimer) clearTimeout(focusTimer);
    };
  }, [companyId]);


  const isCompanyAdmin = companyRole === 'company_admin' || companyRole === 'super_admin';

  return { wallet, loading, companyId, isCompanyAdmin };
}
